const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? 'http://localhost:8787';
const SHOP_BASE = import.meta.env.VITE_SHOP_URL ?? 'http://localhost:8789';
const ORDER_BASE = import.meta.env.VITE_ORDER_URL ?? 'http://localhost:8791';
const SUPPORT_BASE = import.meta.env.VITE_SUPPORT_URL ?? 'http://localhost:8792';
const RESERVATION_BASE = import.meta.env.VITE_RESERVATION_URL ?? 'http://localhost:8793';

async function request<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: init?.body instanceof FormData ? (init?.headers ?? {}) : { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error ?? `http_${res.status}`);
    (err as Error & { status?: number; data?: unknown }).status = res.status;
    (err as Error & { status?: number; data?: unknown }).data = data;
    throw err;
  }
  return data as T;
}

export const authApi = {
  me: () => request<{ ok: true; user: { id: string; email: string; role: string; shop_id: string | null } }>(
    AUTH_BASE,
    '/auth/me',
  ),
  login: (email: string, password: string) =>
    request<{ ok: true; user: { id: string; email: string; role: string; shop_id: string | null } }>(
      AUTH_BASE,
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
  logout: () => request<{ ok: true }>(AUTH_BASE, '/auth/logout', { method: 'POST' }),
  getInvitation: (token: string) =>
    request<{
      ok: true;
      invitation: { email: string; expires_at: string };
      shop: {
        id: string;
        name: string;
        slug: string;
        shop_type: import('@reims-local/types').ShopType;
        address: string | null;
        phone: string | null;
        partner_included: boolean;
      };
    }>(AUTH_BASE, `/auth/invitation/${encodeURIComponent(token)}`),
  activateInvitation: (
    token: string,
    body: { password: string; name: string; phone?: string; cgu_version: string },
  ) =>
    request<{ ok: true; shop_id: string }>(AUTH_BASE, `/auth/invitation/${encodeURIComponent(token)}/activate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export type ShopResponse = import('@reims-local/types').Shop;
export type ProductResponse = import('@reims-local/types').Product;

export const shopApi = {
  me: () => request<{ ok: true; data: ShopResponse }>(SHOP_BASE, '/shop/me'),
  update: (input: Partial<ShopResponse> & { opening_hours?: import('@reims-local/types').OpeningHours | null }) =>
    request<{ ok: true; data: ShopResponse }>(SHOP_BASE, '/shop/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  listProducts: () => request<{ ok: true; data: ProductResponse[] }>(SHOP_BASE, '/shop/me/products'),
  createProduct: (input: Partial<ProductResponse>) =>
    request<{ ok: true; data: ProductResponse }>(SHOP_BASE, '/shop/me/products', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateProduct: (id: string, input: Partial<ProductResponse>) =>
    request<{ ok: true; data: ProductResponse }>(SHOP_BASE, `/shop/me/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteProduct: (id: string) =>
    request<{ ok: true }>(SHOP_BASE, `/shop/me/products/${id}`, { method: 'DELETE' }),
  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ ok: true; data: { photo_url: string } }>(
      SHOP_BASE,
      `/shop/me/products/${id}/photo`,
      { method: 'POST', body: form },
    );
  },
};

export type OrderResponse = import('@reims-local/types').Order;
export type OrderItemResponse = import('@reims-local/types').OrderItem;

export const orderApi = {
  list: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<{
      ok: true;
      data: Array<{
        order: OrderResponse;
        buyer: { id: string; name: string | null; email: string; phone: string | null };
      }>;
    }>(ORDER_BASE, `/shop/me/orders${q}`);
  },
  get: (id: string) =>
    request<{
      ok: true;
      data: {
        order: OrderResponse;
        items: Array<OrderItemResponse & { product: { name: string; category: string | null; photo_url: string | null } | null }>;
        shop: { id: string; name: string; slug: string; address: string | null; phone: string | null } | null;
        buyer: { id: string; name: string | null; email: string; phone: string | null } | null;
      };
    }>(ORDER_BASE, `/orders/${id}`),
  patch: (id: string, status: import('@reims-local/types').OrderStatus, cancel_reason?: string) =>
    request<{ ok: true; data: OrderResponse }>(ORDER_BASE, `/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, cancel_reason }),
    }),
};

export type SupportTicketResponse = import('@reims-local/types').SupportTicket;
export type SupportMessageResponse = import('@reims-local/types').SupportMessage;

export const supportApi = {
  list: () => request<{ ok: true; data: SupportTicketResponse[] }>(SUPPORT_BASE, '/support/me/tickets'),
  create: (input: { category: import('@reims-local/types').SupportCategory; subject: string; message: string }) =>
    request<{ ok: true; data: { id: string; ticket_number: string } }>(SUPPORT_BASE, '/support/me/tickets', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  get: (id: string) =>
    request<{ ok: true; data: { ticket: Omit<SupportTicketResponse, 'admin_note'>; messages: SupportMessageResponse[] } }>(
      SUPPORT_BASE,
      `/support/me/tickets/${id}`,
    ),
  reply: (id: string, content: string) =>
    request<{ ok: true }>(SUPPORT_BASE, `/support/me/tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  resolve: (id: string) =>
    request<{ ok: true }>(SUPPORT_BASE, `/support/me/tickets/${id}/resolve`, { method: 'POST' }),
};

export const activityApi = {
  exportUrl: (period: '24h' | '7d' | '30d', format: 'json' | 'csv') =>
    `${SHOP_BASE}/shop/me/activity?period=${period}&format=${format}`,
};

export type ReservationResponse = import('@reims-local/types').Reservation;

export const reservationApi = {
  list: (params: { status?: string; from?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.from) qs.set('from', params.from);
    const q = qs.toString();
    return request<{
      ok: true;
      data: Array<{
        reservation: ReservationResponse;
        buyer: { id: string; name: string | null; email: string; phone: string | null; no_show_count: number };
      }>;
    }>(RESERVATION_BASE, `/shop/me/reservations${q ? `?${q}` : ''}`);
  },
  get: (id: string) =>
    request<{
      ok: true;
      data: {
        reservation: ReservationResponse;
        shop: { id: string; name: string; slug: string; address: string | null; phone: string | null } | null;
        buyer: { id: string; name: string | null; email: string; phone: string | null; no_show_count: number } | null;
      };
    }>(RESERVATION_BASE, `/reservations/${id}`),
  patch: (id: string, status: 'confirmed' | 'cancelled' | 'no_show', cancel_reason?: string) =>
    request<{ ok: true; data: ReservationResponse }>(RESERVATION_BASE, `/reservations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, cancel_reason }),
    }),
};
